// ─────────────────────────────────────────────────────────────────────────────
// PreferredProductsEffects
// ─────────────────────────────────────────────────────────────────────────────
// NgRx effects for preferred product operations.
// Responsibilities:
//   - Load paginated preferred products list from API
//   - Create and update preferred products
//   - Bulk-delete preferred products
//   - Show toast notifications on success/error
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { CommonService } from 'src/app/shared/common.service';
import { TranslateService } from '@ngx-translate/core';
import { PreferredProductService } from '../services/preferred-product.service';
import { IPreferredProduct } from '../models/preferred-product.models';
import * as preferredProductsActions from './preferred-products.actions';

@Injectable()
export class PreferredProductsEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private preferredProductService: PreferredProductService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  // ── Load preferred products ──────────────────────────────────────────────

  LoadPreferredProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(preferredProductsActions.LoadPreferredProducts),
      switchMap((action) =>
        this.preferredProductService.listPreferredProducts(action.page, action.key, action.filters, action.sortBy, action.sortMode).pipe(
          map((res: APIResponse<IPreferredProduct>) => preferredProductsActions.LoadPreferredProductsSuccess({ response: res })),
          catchError(() => of(preferredProductsActions.LoadPreferredProductsError())),
        ),
      ),
    ),
  );

  // ── Create preferred product ─────────────────────────────────────────────

  CreatePreferredProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(preferredProductsActions.CreatePreferredProduct),
      switchMap((action) =>
        this.preferredProductService.createPreferredProduct(action.payload).pipe(
          map((res: APIResponse<IPreferredProduct>) => preferredProductsActions.CreatePreferredProductSuccess({ preferredProduct: res.data[0] })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.preferred_products.toast.create_success'), 'SUCCESS');
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            return of(preferredProductsActions.CreatePreferredProductError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Update preferred product ─────────────────────────────────────────────

  UpdatePreferredProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(preferredProductsActions.UpdatePreferredProduct),
      switchMap((action) =>
        this.preferredProductService.updatePreferredProduct(action.id, action.payload).pipe(
          map((res: APIResponse<IPreferredProduct>) => preferredProductsActions.UpdatePreferredProductSuccess({ preferredProduct: res.data[0] })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.preferred_products.toast.update_success'), 'SUCCESS');
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            return of(preferredProductsActions.UpdatePreferredProductError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Bulk delete preferred products ───────────────────────────────────────

  BulkDeletePreferredProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(preferredProductsActions.BulkDeletePreferredProducts),
      switchMap((action) =>
        this.preferredProductService.bulkDeletePreferredProducts(action.ids).pipe(
          map(() => preferredProductsActions.BulkDeletePreferredProductsSuccess()),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.preferred_products.toast.bulk_delete_success'), 'SUCCESS');
          }),
          catchError(() => of(preferredProductsActions.BulkDeletePreferredProductsError())),
        ),
      ),
    ),
  );

  // ── Helpers ──────────────────────────────────────────────────────────────

  private resolveErrorMessage(error: HttpErrorResponse): string {
    const status = error?.status;
    const backendMessage = error?.error?.message || error?.message || '';

    if (status === 409) {
      return this.translateService.instant('knowledge_base.preferred_products.toast.duplicate');
    }
    if (status === 400 && /cannot be edited|order-sourced/i.test(backendMessage)) {
      return this.translateService.instant('knowledge_base.preferred_products.toast.order_sourced_read_only');
    }
    return backendMessage || this.translateService.instant('knowledge_base.preferred_products.toast.error');
  }
}
